import { Trans, useLingui } from '@lingui/react/macro'
import { TooltipTimestamp } from 'features/timeSeries/components/TooltipTimestamp'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { useCursorData } from 'features/timeSeries/CursorDataContext'
import { useSentinelOverlayMap } from 'features/timeSeries/hooks/useSentinelOverlayMap'
import {
  expectedAtTime,
  SENTINEL_OUT_OF_BAND_COLOR,
  SIGMA_WARN,
  sigmaAtTime,
} from 'features/timeSeries/hooks/useSentinelScores'
import { formatCurrentValue } from 'features/timeSeries/utils/scatterPlotTableHelpers'
import React, { useLayoutEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { SensorScatterplotData } from 'types'

const CURSOR_OFFSET_PX = 15

/*
 * Deliberately NOT a Radix tooltip. This readout follows the cursor, so during
 * hover/drag the pointer routinely overtakes it. Radix portals its content
 * inside a positioner wrapper div that our styles cannot reach — even with
 * `pointer-events: none` on the content, hit testing falls through to the
 * same-sized wrapper, which still steals the pointer. That fires mouseleave on
 * the chart area (unmounting the readout and, mid-drag, snapping uPlot's
 * selection edge to the plot boundary) and re-fires mousemove once it unmounts:
 * a flicker loop. A plain fixed-position div that is pointer-transparent
 * end-to-end has no hit-testing surface by construction.
 */
const ReadoutBox = styled.div`
  position: fixed;
  z-index: 1000;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 12px;
  font-family: Manrope;
  line-height: 1;
  color: white;
  background: ${CHART_COLORS.TOOLTIP_BG};
  border: 1px solid ${CHART_COLORS.TOOLTIP_BORDER};
  box-shadow:
    hsl(206 22% 7% / 35%) 0px 10px 38px -10px,
    hsl(206 22% 7% / 20%) 0px 10px 20px -15px;
  user-select: none;
  pointer-events: none;
`

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  &:last-child {
    margin-bottom: 0;
  }
`

const ColorDot = styled.div<{ color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${({ color }) => color};
`

const SensorName = styled.span`
  color: ${CHART_COLORS.TOOLTIP_NAME_COLOR};
`

const ValueText = styled.span`
  color: ${CHART_COLORS.TOOLTIP_VALUE_COLOR};
  font-weight: 600;
`

const SigmaText = styled.span<{ $outOfBand: boolean }>`
  color: ${({ $outOfBand }) =>
    $outOfBand ? SENTINEL_OUT_OF_BAND_COLOR : CHART_COLORS.TOOLTIP_NAME_COLOR};
  font-weight: 600;
  white-space: nowrap;
`

interface InterpolatedValueTooltipProps {
  cursorPosition: {
    x: number
    y: number
    clientX: number
    clientY: number
  } | null
  tableData: SensorScatterplotData[]
  sensorVisibilityConfig: Record<string, boolean>
}

export const InterpolatedValueTooltip: React.FC<
  InterpolatedValueTooltipProps
> = ({ cursorPosition, tableData, sensorVisibilityConfig }) => {
  const { t } = useLingui()

  const { cursorData } = useCursorData()
  const overlaysByTarget = useSentinelOverlayMap()
  const boxRef = useRef<HTMLDivElement>(null)

  const shouldShow = useMemo(
    () => cursorPosition && Object.keys(cursorData).length > 0,
    [cursorPosition, cursorData]
  )

  // Position synchronously before paint: right of the cursor, flipping to the
  // left when the box would overflow the viewport, clamped vertically.
  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el || !cursorPosition) return
    const rect = el.getBoundingClientRect()
    let left = cursorPosition.clientX + CURSOR_OFFSET_PX
    if (left + rect.width > window.innerWidth) {
      left = cursorPosition.clientX - CURSOR_OFFSET_PX - rect.width
    }
    const top = Math.max(
      0,
      Math.min(cursorPosition.clientY, window.innerHeight - rect.height)
    )
    el.style.left = `${Math.max(0, left)}px`
    el.style.top = `${top}px`
  })

  if (!shouldShow || !cursorPosition) {
    return null
  }

  return (
    <ReadoutBox
      ref={boxRef}
      style={{
        left: cursorPosition.clientX + CURSOR_OFFSET_PX,
        top: cursorPosition.clientY,
      }}>
      <TooltipTimestamp tableData={tableData} />
      {tableData.map((item) => {
        if (sensorVisibilityConfig[item.id] === false) return null

        const [valStr, unitStr] = formatCurrentValue(
          item.currentValue,
          item.unit,
          item.stateLabel
        )

        const overlay = overlaysByTarget.get(item.id)
        const timestampMs =
          typeof item.xValue === 'number'
            ? item.xValue
            : typeof item.xValue === 'string'
              ? new Date(item.xValue).getTime()
              : null
        const sigma =
          overlay && timestampMs !== null
            ? sigmaAtTime(overlay, timestampMs)
            : null
        const expected =
          overlay && timestampMs !== null
            ? expectedAtTime(overlay, timestampMs)
            : null
        const outOfBand = sigma !== null && Math.abs(sigma) >= SIGMA_WARN
        const [expectedStr, expectedUnitStr] =
          expected !== null ? formatCurrentValue(expected, item.unit) : ['', '']

        return (
          <React.Fragment key={item.id}>
            {overlay && expected !== null && !overlay.centerPenId && (
              <TooltipRow>
                <ColorDot color={SENTINEL_OUT_OF_BAND_COLOR} />
                <SensorName>
                  {overlay.sentinelName}
                  <Trans> (predicted):</Trans>
                </SensorName>
                <ValueText>
                  {expectedStr} {expectedUnitStr}
                </ValueText>
              </TooltipRow>
            )}
            <TooltipRow>
              <ColorDot color={item.color} />
              <SensorName>{item.name}:</SensorName>
              <ValueText>
                {valStr} {unitStr}
              </ValueText>
              {sigma !== null && (
                <SigmaText $outOfBand={outOfBand}>
                  {sigma > 0 ? '▲' : '▼'} {Math.abs(sigma).toFixed(1)}σ
                  {outOfBand ? t` out of band` : ''}
                </SigmaText>
              )}
            </TooltipRow>
          </React.Fragment>
        )
      })}
    </ReadoutBox>
  )
}
